import React, { useContext, useEffect, useState } from 'react';

import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

import DropzoneFileUploader from '../../../../../shared/components/DropzoneFileUploader/DropzoneFileUploader';
import CustomModalNE from '../../../../../shared/components/ModalNE/CustomModalNE';
import StyledButtonNE from '../../../../../shared/components/StyledButtonNE/StyledButtonNE';
import { BUSINESS_IMAGE_UPLOADER_VALIDATORS_NE } from '../../../../../shared/constants/settings';
import {
  CLICK_ACTION,
  EDIT_COMPANY,
  EDIT_HOLDING,
  SECTION_CONFIGURATION,
  SUB_SECTION_PASS_CANCEL,
  SUB_SECTION_PASS_CONFIRM,
  SUB_SECTION_PASS_REQUEST
} from '../../../../../shared/constants/tagManager';
import {
  UPDATE_BUSINESS_COMPANY_ERROR_TOAST,
  UPDATE_BUSINESS_COMPANY_SUCCESS_TOAST,
  UPDATE_BUSINESS_HOLDING_ERROR_TOAST,
  UPDATE_BUSINESS_HOLDING_SUCCESS_TOAST
} from '../../../../../shared/constants/toast';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { IChangeBusinessDataResponse } from '../../../../../shared/interfaces/businessData';
import Company from '../../../../../shared/services/api/companies/companies';
import Holding from '../../../../../shared/services/api/holdings/holdings';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_SETTINGS } from '../../../../../shared/translations/settings';
import { GenericObject, GenericVoidFunction } from '../../../../../shared/types/genericTypes';

import './BusinessDataChangeModal.scss';

interface BusinessDataChangeModalProps {
  isModalOpen: boolean;
  handleCloseModal: GenericVoidFunction;
  id: number | string;
  name: string;
  isHolding: boolean;
}

const BusinessDataChangeModal = ({
  isModalOpen,
  handleCloseModal,
  id,
  isHolding,
  name
}: BusinessDataChangeModalProps) => {
  const [businessName, setBusinessName] = useState(name);
  const [businessImage, setBusinessImage] = useState('');
  const [translation, setTranslation] = useState<GenericObject>({});
  const [loading, setLoading] = useState(false);

  const { updateUserHolding, updateUserCompany } = useContext(CompanyContext);

  const { t } = useTranslation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBusinessName(e.target.value);
  };

  const makeRequest = async () => {
    try {
      setLoading(true);
      let response: IChangeBusinessDataResponse;
      const businessData = getFieldsToUpdate(businessImage, businessName);
      if (isHolding) {
        response = await Holding.patchHoldingAttributes(id as number, businessData);
        updateUserHolding(response);
        TagManagerService.pushEvent(CLICK_ACTION, EDIT_HOLDING, {
          ...SECTION_CONFIGURATION,
          ...SUB_SECTION_PASS_CONFIRM
        });
        return handleCloseModal(UPDATE_BUSINESS_HOLDING_SUCCESS_TOAST);
      }
      response = await Company.patchCompanyAttributes(id as string, businessData);
      updateUserCompany(response);
      TagManagerService.pushEvent(CLICK_ACTION, EDIT_COMPANY, {
        ...SECTION_CONFIGURATION,
        ...SUB_SECTION_PASS_CONFIRM
      });
      return handleCloseModal(UPDATE_BUSINESS_COMPANY_SUCCESS_TOAST);
    } catch (error) {
      const toastData = isHolding ? UPDATE_BUSINESS_HOLDING_ERROR_TOAST : UPDATE_BUSINESS_COMPANY_ERROR_TOAST;
      handleCloseModal(toastData);
    } finally {
      setLoading(false);
    }
  };

  const getFieldsToUpdate = (businessImage: string, businessName: string) => {
    const businessAttributes: any = {};
    if (businessName !== name && businessName !== '') {
      businessAttributes.name = businessName;
    }
    if (businessImage) {
      businessAttributes.image = businessImage;
    }
    return businessAttributes;
  };

  const onModalClose = () => {
    const gtmDescription = isHolding ? EDIT_HOLDING : EDIT_COMPANY;
    TagManagerService.pushEvent(CLICK_ACTION, gtmDescription, { ...SECTION_CONFIGURATION, ...SUB_SECTION_PASS_CANCEL });
    handleCloseModal();
    setBusinessName('');
  };

  const updateBase64 = (fileBase64: string) => {
    setBusinessImage(fileBase64);
  };

  const prefixfilesInfo = (): string => {
    return `${name} / image`;
  };

  useEffect(() => {
    if (isHolding) {
      TagManagerService.pushEvent(CLICK_ACTION, EDIT_HOLDING, {
        ...SECTION_CONFIGURATION,
        ...SUB_SECTION_PASS_REQUEST
      });
      return setTranslation({
        title: TRANSLATIONS_SETTINGS.businessData.modal.title.holding,
        name: TRANSLATIONS_SETTINGS.businessData.modal.name.holding,
        logo: TRANSLATIONS_SETTINGS.businessData.modal.logo.holding
      });
    }
    TagManagerService.pushEvent(CLICK_ACTION, EDIT_COMPANY, { ...SECTION_CONFIGURATION, ...SUB_SECTION_PASS_REQUEST });
    return setTranslation({
      title: TRANSLATIONS_SETTINGS.businessData.modal.title.company,
      name: TRANSLATIONS_SETTINGS.businessData.modal.name.company,
      logo: TRANSLATIONS_SETTINGS.businessData.modal.logo.company
    });
  }, [isHolding]);

  const disableButton = () => {
    return ((!businessName || businessName === name) && !businessImage) || loading;
  };

  return (
    <CustomModalNE open={isModalOpen} handleClose={onModalClose}>
      <div className="business-data-change-content-ne">
        <div className="business-data-change-title">{t(translation.title)}</div>
        <div className="business-data-change-form">
          <div className="business-data-change-fields">
            <TextField
              variant="standard"
              className="input"
              label={t(translation.name)}
              id="name"
              name="name"
              type="text"
              value={businessName}
              onChange={handleChange}
            />
          </div>
          <div className="business-data-change-logo">
            {t(translation.logo)}
            <DropzoneFileUploader
              updateBase64={(base64File) => updateBase64(base64File)}
              customData={{ prefixfilesInfo: prefixfilesInfo() }}
              validator={BUSINESS_IMAGE_UPLOADER_VALIDATORS_NE}
              type="image"
            />
          </div>
          <div className="business-data-change-buttons">
            <StyledButtonNE
              buttonType="primary"
              classes="business-data-change-button"
              disabled={disableButton()}
              loading={loading}
              buttonSize="md"
              action={makeRequest}
            >
              {t(TRANSLATIONS_SETTINGS.businessData.modal.saveButton)}
            </StyledButtonNE>
            <StyledButtonNE
              buttonType="secondary"
              classes="business-data-change-button"
              disabled={loading}
              buttonSize="md"
              action={onModalClose}
            >
              {t(TRANSLATIONS_SETTINGS.businessData.modal.cancelButton)}
            </StyledButtonNE>
          </div>
        </div>
      </div>
    </CustomModalNE>
  );
};

export default BusinessDataChangeModal;
